import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectorRef, Component, DoCheck, ElementRef, Input, OnChanges, OnDestroy, OnInit, QueryList, Renderer2, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ChartOptions } from '../grouping-view/grouping-view.component';
import { addDays, addMonths, addQuarters, addYears, areIntervalsOverlapping, differenceInDays, differenceInMonths, differenceInYears, eachDayOfInterval, eachMonthOfInterval, eachYearOfInterval, endOfMonth, endOfQuarter, endOfYear, format, getDayOfYear, getDaysInMonth, getHours, getMinutes, getMonth, getQuarter, getSeconds, getYear, hoursToMilliseconds, isBefore, isFirstDayOfMonth, isSameDay, isSameHour, isSameMonth, isWithinInterval, lastDayOfMonth, lastDayOfQuarter, lastDayOfYear, Locale, millisecondsInHour, monthsInYear, secondsInDay, set, setYear, startOfDay, startOfMonth, startOfQuarter, startOfYear, subMonths, subQuarters, subWeeks } from 'date-fns';
import * as _ from 'lodash';
import { OpportunitiesService } from 'src/app/services/opportunities.service';
import { GroupingsService } from 'src/app/services/groupings.service';
import { FilterOperator, ISAPI, ISChartComponent, ISFilterAPI, ISPagination, ISSelectComponent, SpinnerService } from 'is-lib';
import { ServerResponse } from 'is-lib/lib/models/ServerResponse';
import { BehaviorSubject, catchError, distinctUntilChanged, forkJoin, map, of, pairwise, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { EnergyMetersService } from 'src/app/services/energyMeters.service';
import { environment } from 'src/environments/environment';
import { UsersService } from 'src/app/services/users.service';
import { PriceService } from 'src/app/services/prices.service';
import { ContractPrice, ContractPriceHistory, GroupingSimulation } from 'src/app/models/ContractPrice';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TranslateService } from '@ngx-translate/core';
import { IElectricityOmipPricesPT, IElectricityOmipPricesPTHistory } from 'src/app/models/OmipPrice';
import { TicketsService } from 'src/app/services/tickets.service';
import { SnackBarService } from 'src/app/services/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { PricePeriods } from 'src/app/models/PricePeriod';
import { es, pt } from 'date-fns/locale';
import { CoverProposalDialog } from './cover-proposal-dialog/cover-proposal-dialog.component';
import { SupplierProductEconomicConditionsService } from 'src/app/services/supplierProductEconomicConditions.service';
import { ElectricityPrice } from 'src/app/models/EnergyPrice';
import { ElectricityPricesService } from 'src/app/services/electricity-prices.service';
import { EnergyUnits, EnergyUnitService } from 'src/app/services/energy-unit.service';
import { ApexAxisChartSeries, ApexGrid, ApexLegend, ApexOptions, ApexTooltip, ApexXAxis, ApexYAxis, XAxisAnnotations } from 'apexcharts';
import { ConsumptionMonthData, ConsumptionQuarterData, ConsumptionYearData, CustomChartTooltips, ElectricityPriceWeighting, HEDGING_SERIES_COLORS, IndexedPriceEstimate, LockedCostPart, OmipTrend, PriceContainer, PricePeriodsSimulation } from './hedging-view';
import { CdkDragDrop, CdkDragEnd, CdkDragMove, DragRef } from '@angular/cdk/drag-drop';
import { IndexedPriceFormulaInfoComponent } from './indexed-price-formula-info/indexed-price-formula-info.component';
import { CoverDialogComponent } from './cover-dialog/cover-dialog.component';
import { BasePriceBreakdownComponent } from './base-price-breakdown/base-price-breakdown.component';
import { IGrouping } from 'src/app/models/Grouping';
import { electricityAllowedConsumptions } from 'src/app/constants';
import { SnackBarType } from 'src/app/models/Snackbar';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ContractsService } from 'src/app/services/contracts.service';
import { ElectricitySupplyContractEconomicCondition } from 'src/app/models/ElectricitySupplyContracts';
import { IContract } from 'src/app/models/ContractResponse';


@Component({
    selector: 'app-hedging-view',
    templateUrl: './hedging-view.component.html',
    styleUrls: ['./hedging-view.component.scss']
})
export class HedgingViewComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
    private readonly quartersAfterCurrent: number = 3

    private static readonly MARKET_STEP_SEEN_STORAGE_KEY = 'hedging-view-market-step-seen'
    static readonly SERIES_COLORS = HEDGING_SERIES_COLORS
    readonly COVER_STEP_PERCENTAGES = [25, 50, 75, 100]

    protected DISPLAY_MONTHS = eachMonthOfInterval({start: startOfYear(new Date()), end: endOfYear(new Date())}).map( (month) => format(month, 'MMM', { locale: this.dateFormatLocale }) )

    @Input() selectedOpportunity?: any;
    @Input() selectedGrouping!: string;
    @Input() opportunityId!: string;
    // Set instead of opportunityId when simulating on an existing contract
    @Input() contractId?: string | null;
    @Input() selectedContract?: IContract | null;
    @Input() selectedMarket!: 'Electricity' | 'NaturalGas';

    @ViewChildren(ISSelectComponent) ISSelectList?: QueryList<ISSelectComponent>
    @ViewChildren(MatExpansionPanel) cardExpansionPanels?: QueryList<MatExpansionPanel>
    // Tracks the year drop zones so drag-and-drop can detect when a price period is moved into a valid target year.
    @ViewChild('yearDropContainer') set yearDropContainer(container: ElementRef) {
        if (container) {
            let typedContainer = container.nativeElement as HTMLDivElement
            
            this.dropContainersBoundingBoxes = new Map()

            Array.from(typedContainer.getElementsByClassName('year-drop-container')).forEach( (childDiv) => {
                let div = childDiv as HTMLElement;
                this.dropContainersBoundingBoxes.set(div.id, div)
            } )
        }
    }

    dragInYearFlag$: BehaviorSubject<PricePeriodsSimulation | undefined> = new BehaviorSubject<PricePeriodsSimulation | undefined>(undefined);
    dragOutYearFlag$: BehaviorSubject<PricePeriodsSimulation | undefined> = new BehaviorSubject<PricePeriodsSimulation | undefined>(undefined);
    pricePeriodCoverChangeFlag$: Subject<void> = new Subject<void>();
    // Ends the subscriptions to streams that outlive this component (translations, the parent's price updates)
    private readonly _destroy$ = new Subject<void>();

    // Active electricity economic conditions of the selected contract's product/general/particular conditions (null while loading or without a contract)
    contractElecEconomicConditionIds: number[] | null = null;
    // Details (ProductsElectricityEconomicConditions/{id}) of the contract's active electricity economic conditions that have price lines or versions
    contractElecEconomicConditionsWithPrices: any[] = [];
    private _contractEconomicConditionsSubscription?: Subscription;

    translationStream: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    dropContainersBoundingBoxes: Map<string, HTMLElement> = new Map()
    pricePeriodsSimulation: PricePeriodsSimulation[] = []
    consumptionsByDateTypes: ConsumptionYearData[] = [];

    newDisplayMode: 'Covers' | 'Consumptions' = 'Covers'
    showChart: boolean = false;
    showHelp: boolean = false;
    // Price whose remove button is hovered, used to hide the block's "change percentage" tooltip meanwhile
    hoveredRemovePriceId: string | null = null;
    // The market step starts open on the first visit only, so returning users go straight to choosing prices
    marketStepExpanded: boolean = !this.readMarketStepSeen();
    selectedPrice?: number;

    selectedOmipPrice: IElectricityOmipPricesPT | null = null;
    omipPrices: IElectricityOmipPricesPT[] = [];
    // Computed once when OMIP prices load, since the history can be long and the template reads it every change detection
    omipTrendsById: Map<string, OmipTrend | null> = new Map();
    // Names have _{number} prefix to indicate the order of the intervals
    omipIntervalPriceData: { 
        _1_currentQuarter: IElectricityOmipPricesPT | null,
        _2_year: IElectricityOmipPricesPT | null, 
        _3_quarter: IElectricityOmipPricesPT | null, 
        _4_month: IElectricityOmipPricesPT | null
    } = { _1_currentQuarter: null, _2_year: null, _3_quarter: null, _4_month: null };
    omipChart?: ISChartComponent;
    pricesChart?: ISChartComponent;

    pricePeriodSelectionModel: SelectionModel<PricePeriods.ElectricityPrices> = new SelectionModel<PricePeriods.ElectricityPrices>(true, [], true, (p1, p2) => p1.id == (p2?.id ?? '') );
    availableYears: number[] = Array.from({ length: 6 }, (_, index) => getYear(new Date()) + index);
    displayYearSelectionModel: SelectionModel<number> = new SelectionModel<number>(true, [getYear(new Date())]);

    economicConditionsFilterForm = new FormGroup({
        spGeneralConditionId: new FormControl<number | null>(null),
        spElectricityEconomicConditionId: new FormControl<number | null>(null),
        spNaturalGasEconomicConditionId: new FormControl<number | null>(null),
        spParticularConditionId: new FormControl<number | null>(null),
        supplierProductId: new FormControl<number | null>(null)
    });

    omipPriceChartOptions: ApexOptions = {
        chart: {
            type: 'line',
            height: 220,
            zoom: {
                enabled: true,
            }
        },
        markers: {
            shape: 'circle',
            size: 1,
            fillOpacity: 1,
            strokeOpacity: 1,
            strokeColors: '#4CA2FA',
            colors: ['#ffffffff'],
            hover: {
                sizeOffset: 4
            }
        },
        xaxis: {
            type: 'datetime',
            axisBorder: {
                color: '#b2b1b0',
                show: true
            },
            min: subMonths(startOfDay(new Date()), 1).getTime(),
            axisTicks: {
                show: true,
                color: '#b2b1b0'
            }
        },
        yaxis: {
            axisBorder: {
                color: '#b2b1b0',
                show: true
            },
            axisTicks: {
                show: true,
                color: '#b2b1b0'
            },
            labels: {
                formatter: (val) => { return val.toFixed(this.energyUnitService.priceUnit == 'kWh' ? 4 : 2) }
            }
        },
        grid: {
            xaxis: {
                lines: {
                    show: true
                }
            }
        },
        series: [],
        stroke: {
            curve: 'smooth',
            width: 1
        },
        tooltip: {
            fillSeriesColor: false,
            x: {
                show: true,
            },
            shared: false,
            intersect: false,
            marker: {
                show: true
            }
        }
    }

    // Months shown in the simulation chart, in x-axis order (used by the tooltip and the axis labels)
    simulationChartMonths: { date: Date, label: string, data: ConsumptionMonthData }[] = [];

    // Consumption areas (total behind, protected in front, so the blue share is the coverage) on the MWh axis,
    // and locked, indexed and average price lines on the €/MWh axis.
    // Per-series arrays follow the series order returned by fillEquipmentSeries.
    simulationChartOptions: ApexOptions = {
        chart: {
            type: 'line',
            height: 420,
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        stroke: {
            curve: ['monotoneCubic', 'monotoneCubic', 'straight', 'straight', 'straight'] as any,
            width: [1.5, 1.5, 2.5, 2, 3],
            dashArray: [0, 0, 0, 5, 0],
            lineCap: 'round'
        },
        fill: {
            type: 'solid',
            opacity: [0.45, 0.5, 1, 1, 1]
        },
        // Markers keep single-month prices visible (a line needs two points) and show the monthly granularity
        markers: {
            size: [0, 0, 3, 0, 3],
            strokeWidth: 1.5,
            strokeColors: '#ffffff',
            hover: {
                sizeOffset: 2
            }
        },
        legend: this.getSimulationChartLegend(),
        grid: this.getSimulationChartGrid(),
        xaxis: this.getSimulationChartXAxis(),
        yaxis: this.getSimulationChartYAxes(),
        series: [],
        tooltip: this.getSimulationChartTooltip(),
        annotations: {},
        dataLabels: {
            enabled: false
        }
    }

    constructor(
        private _userService: UsersService,
        private _priceService: PriceService,
        private _translationService: TranslateService,
        private _dialog: MatDialog,
        private _electricityPricesService: ElectricityPricesService,
        private _renderer2: Renderer2,
        private _groupingsService: GroupingsService,
        private _snackbarService: SnackBarService,
        private _router: Router,
        private _contractsService: ContractsService,
        private _economicConditionsService: SupplierProductEconomicConditionsService,
        public energyUnitService: EnergyUnitService
    ) {
        this._translationService.stream([
            'TOTAL_CONSUMPTIONS', 'CONSUMPTION', 'PRICE', 'TOTAL_CONSUMPTION', 'FIXED_PRICE', 'COVER_VALUES', 'OMIP_PRICE', 'TOTAL_CONSUMPTION', 'TOTAL_CONSUMPTION_25', 
            'TOTAL_CONSUMPTION_50', 'TOTAL_CONSUMPTION_75', 'COVER_PRICE', 'OMIP_PRICE_PROJECTION', 'FINAL_PRICE', 'PROTECTED_CONSUMPTION',
            'CHART_CONSUMPTION_AXIS', 'CHART_PRICE_AXIS', 'TOOLTIP_ESTIMATED_CONSUMPTION', 'TOOLTIP_PROTECTED_AT', 'TOOLTIP_REST_AT_INDEXED', 'TOOLTIP_AVERAGE_PRICE',
            'TOOLTIP_NOTHING_PROTECTED'
        ]).pipe( takeUntil(this._destroy$) ).subscribe({
            next: (str) => {
                this.translationStream.next(str);
                // New references make is-chart re-render, so the legend and axis titles pick up the translated names
                this.simulationChartOptions.legend = this.getSimulationChartLegend();
                this.simulationChartOptions.yaxis = this.getSimulationChartYAxes();
                this.omipPriceChartOptions.yaxis = { ...this.omipPriceChartOptions.yaxis, title: { text: this.translateWithUnit(str['CHART_PRICE_AXIS']) } };
            }
        });

        // Charts hold the values already converted, so they're rebuilt when the unit changes (the rest of the page converts in the template)
        this.energyUnitService.units$.pipe( takeUntil(this._destroy$) ).subscribe(() => {
            this.simulationChartOptions.legend = this.getSimulationChartLegend();
            this.simulationChartOptions.yaxis = this.getSimulationChartYAxes();

            const translations = this.translationStream.value;
            if (translations) {
                this.omipPriceChartOptions.yaxis = { ...this.omipPriceChartOptions.yaxis, title: { text: this.translateWithUnit(translations['CHART_PRICE_AXIS']) } };
            }

            this.selectOmipPrice(this.selectedOmipPrice);
            this.refreshSimulationChartsWithEquipments();
        });
    }

    // Changes with either unit: the charts are recreated when it does
    get energyUnitsKey(): string {
        return this.energyUnitService.consumptionUnit + '|' + this.energyUnitService.priceUnit;
    }

    // Texts with a {{unit}} (the consumption unit, MWh or kWh) or a {{priceUnit}} (€/MWh or €/kWh) placeholder, as chosen by the user
    private translateWithUnit(text: string | undefined): string {
        return (text ?? '')
            .replace(/{{\s*unit\s*}}/g, EnergyUnits.getUnitLabel('consumption', this.energyUnitService.consumptionUnit))
            .replace(/{{\s*priceUnit\s*}}/g, EnergyUnits.getUnitLabel('price', this.energyUnitService.priceUnit));
    }

    // Returns the years currently active in the display selection.
    get selectedYears(): number[] {
        return this.displayYearSelectionModel.selected;
    }

    // Price container layout for a year. The template calls this on every change detection, so the result without a preview is cached until the covers change (see ngDoCheck).
    getSelectedPricesContainerDataByYear(year: number, previewPricePeriod?: PricePeriods.ElectricityPrices): PriceContainer[] {
        if (previewPricePeriod) {
            return this.buildPriceContainersByYear(year, previewPricePeriod);
        }

        let containers = this._priceContainersByYearCache.get(year);

        if (!containers) {
            containers = this.buildPriceContainersByYear(year);
            this._priceContainersByYearCache.set(year, containers);
        }

        return containers;
    }

    // Builds the visual price container layout for a year (the contract's own covers and the selected prices), including a preview container when needed.
    private buildPriceContainersByYear(year: number, previewPricePeriod?: PricePeriods.ElectricityPrices): PriceContainer[] {
        let yearInterval = { start: startOfYear(setYear(new Date(), year)), end: endOfYear(setYear(new Date(), year)) };

        const selectedPricePeriods = [...this.contractCovers, ...this.pricePeriodSelectionModel.selected];
        const hasPreviewPrice = !!previewPricePeriod;

        let selectedPricePeriodsOnYear = _.orderBy(
            [
                ...selectedPricePeriods.filter((pp) =>
                    pp.id !== previewPricePeriod?.id &&
                    areIntervalsOverlapping(yearInterval, { start: pp.deliveryStartDate, end: pp.deliveryEndDate })
                ),
                previewPricePeriod
            ].filter((pp): pp is PricePeriods.ElectricityPrices => !!pp && areIntervalsOverlapping(yearInterval, { start: pp.deliveryStartDate, end: pp.deliveryEndDate })),
            [(pp) => pp.deliveryStartDate, (pp) => pp.deliveryEndDate.getTime() - pp.deliveryStartDate.getTime()],
            ['asc', 'desc']
        );

        const placedContainers: PriceContainer[] = [];

        const hasGridOverlap = (
            candidateColumnStart: number,
            candidateColumnEnd: number,
            candidateRowStart: number,
            candidateRowEnd: number,
            existing: PriceContainer
        ) => {
            const columnsOverlap = candidateColumnStart < existing.columnEnd && candidateColumnEnd > existing.columnStart;
            const rowsOverlap = candidateRowStart < existing.rowEnd && candidateRowEnd > existing.rowStart;
            return columnsOverlap && rowsOverlap;
        };

        return selectedPricePeriodsOnYear.map((pricePeriod) => {
            let priceInterval = { start: pricePeriod.deliveryStartDate, end: pricePeriod.deliveryEndDate };

            if (priceInterval.start < yearInterval.start) {
                priceInterval.start = yearInterval.start;
            } 
            if (priceInterval.end > yearInterval.end) {
                priceInterval.end = yearInterval.end;
            }

            const startMonth = priceInterval.start.getMonth();
            const endMonth = priceInterval.end.getMonth();
            const columnStart = startMonth + 1;
            const columnEnd = endMonth + 2;

            const rowSpan = _.clamp(Math.floor(pricePeriod.coverPercentage / 25), 1, 4);
            const previewRowStart = 1;
            const previewRowEnd = 2;
            const minRegularRowStart = 2;
            const maxRegularRowStart = 6 - rowSpan;
            const baseRegularRowStart = maxRegularRowStart;
            const baseRegularRowEnd = baseRegularRowStart + rowSpan;

            const isPreviewContainer = hasPreviewPrice && previewPricePeriod?.id === pricePeriod.id;

            let selectedRowStart = isPreviewContainer ? previewRowStart : baseRegularRowStart;
            let selectedRowEnd = isPreviewContainer ? previewRowEnd : baseRegularRowEnd;

            if (!isPreviewContainer) {
                for (let candidateRowStart = minRegularRowStart; candidateRowStart <= maxRegularRowStart; candidateRowStart++) {
                    const candidateRowEnd = candidateRowStart + rowSpan;
                    const overlapsExisting = placedContainers.some((existing) =>
                        hasGridOverlap(columnStart, columnEnd, candidateRowStart, candidateRowEnd, existing)
                    );

                    if (!overlapsExisting) {
                        selectedRowStart = candidateRowStart;
                        selectedRowEnd = candidateRowEnd;
                        break;
                    }
                }
            }

            const container = new PriceContainer(
                pricePeriod,
                pricePeriod.coverPercentage,
                columnStart,
                columnEnd,
                selectedRowStart,
                selectedRowEnd,
                pricePeriod.deliveryStartDate.getFullYear(),
                pricePeriod.deliveryEndDate.getFullYear()
            );
            container.isContractCover = this.contractCovers.includes(pricePeriod);

            placedContainers.push(container);

            return container;
        });
    }

    pricePeriodsByDateType: {years: PricePeriods.ElectricityPrices[], quarters: PricePeriods.ElectricityPrices[], months: PricePeriods.ElectricityPrices[], [key: string]: PricePeriods.ElectricityPrices[]} = {
        years: [],
        quarters: [],
        months: []
    }

    @Input() electricityPriceUpdates$!: Subject<PricePeriods.ElectricityPrices[]>

    @Input() currentEquipments: GroupingSimulation.IEquipment[] = [];
    currentElectricityPrices: PricePeriods.ElectricityPrices[] = [];

    // Locked covers built from the selected contract's fixed economic conditions. They are read-only and always applied to the simulation.
    contractCovers: PricePeriods.ElectricityPrices[] = [];
    private _contractCoversSubscription?: Subscription;
    // Supply contract ids the covers were last loaded for (currentEquipments is a new array on every change detection)
    private _contractCoversKey: string = '';

    // Per-year template data, cleared by ngDoCheck when the covers change
    private _priceContainersByYearCache = new Map<number, PriceContainer[]>();
    private _coverByMonthCache = new Map<number, { total: number, contract: number }[]>();
    // State the caches were built from: both arrays are replaced on change (SelectionModel.selected is rebuilt after each select/deselect),
    // while the cover percentages are edited in place
    private _coverCacheState?: { contractCovers: PricePeriods.ElectricityPrices[], selected: PricePeriods.ElectricityPrices[], percentages: string };
    currentNaturalGasPrices: PricePeriods.NaturalGasPrices[] = [];

    // Initializes subscriptions, market data loading, and chart state when the component starts.
    ngOnInit(): void {
        this.writeMarketStepSeen();

        this.displayYearSelectionModel.changed.subscribe( (change) => {
            if (change.removed.at(0) != null && change.source.selected.length == 0) {
                change.source.select(change.removed.at(0)!);
                return;
            }

            // The category x-axis and the year separators follow the generated months
            if (this.currentEquipments.length > 0) {
                this.refreshSimulationCharts();
            }
        } )

        this.dragInYearFlag$.pipe(
            distinctUntilChanged((previous, current) => previous?.year == current?.year),
            pairwise()
        ).subscribe( ( [previousSimulation, currentSimulation] ) => {
            if (currentSimulation) {
                this.createSimulatedDiv(currentSimulation)
            } else {
                if (previousSimulation) {
                    this.removeSimulatedDiv(previousSimulation)
                }
            }
        });
        
        this.getOMIPPrices();

        this.pricePeriodSelectionModel.changed.subscribe( (change) => {
            let priceInterval = this.selectedPricesTotalInterval;

            if (change.added.length > 0 && change.source.selected.length == 1) {
                let price = change.source.selected[0];

                this.economicConditionsFilterForm.setValue({
                    spGeneralConditionId: price.spGeneralConditionId ?? null,
                    spElectricityEconomicConditionId: price.spElectricityEconomicConditionId ?? null,
                    spParticularConditionId: price.spParticularConditionId ?? null,
                    spNaturalGasEconomicConditionId: null,
                    supplierProductId: price.supplierProductId ?? null
                })
            } else if (change.source.selected.length == 0) {
                this.resetEconomicConditionsFilter();
            }
            if (priceInterval) {
                this.displayYearSelectionModel.select(...eachYearOfInterval(priceInterval).flatMap((d) => d.getFullYear()))
            }

            // Also refresh when the last price is removed, otherwise the chart and results keep showing the old cover
            if (this.currentEquipments.length > 0) {
                this.refreshSimulationCharts();
            }
        } )

        this.pricePeriodCoverChangeFlag$.subscribe( () => {
            if (this.currentEquipments.length > 0) {
                this.refreshSimulationCharts();
            }
        })

        this.electricityPriceUpdates$.pipe( takeUntil(this._destroy$) ).subscribe((electricityPrices: PricePeriods.ElectricityPrices[]) => {
            this.currentElectricityPrices = electricityPrices;
            this.mapOmipToElectricityPrices();
            this.refreshSimulationCharts();
            this.recalculatePriceHistoryBasePrices();
            this.pricePeriodSelectionModel.clear();
        })
    }

    // Runs before the template is checked, so the cached cover data is never read stale
    ngDoCheck(): void {
        const selected = this.pricePeriodSelectionModel.selected;
        const percentages = [...this.contractCovers, ...selected].map((pp) => pp.coverPercentage).join(',');
        const state = this._coverCacheState;

        if (!state || state.contractCovers !== this.contractCovers || state.selected !== selected || state.percentages !== percentages) {
            this._coverCacheState = { contractCovers: this.contractCovers, selected, percentages };
            this._priceContainersByYearCache.clear();
            this._coverByMonthCache.clear();
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // Estimates each equipment's consumption for a month using its most recent record for that month index (ignores year).
    getEstimatedMonthConsumptions(month: Date): GroupingSimulation.IEquipmentConsumption[] {
        return this.getEstimatedMonthEquipmentConsumptions(month).map(({ consumption }) => consumption);
    }

    // Latest consumption of each equipment for the month, paired with its equipment (needed for per-meter data such as the voltage level).
    // Months without a record (or with an empty one) use the equipment's average monthly consumption, so they don't count as 0 MWh
    // and the year's cover % and costs keep every month's weight.
    getEstimatedMonthEquipmentConsumptions(month: Date): { equipment: GroupingSimulation.IEquipment, consumption: GroupingSimulation.IEquipmentConsumption }[] {
        return this.currentEquipments.flatMap(equip => {
            const consumptions = (equip.consumptions || []).filter(c => c.totalValue > 0);
            if (consumptions.length === 0) return [];

            const consForMonth = consumptions.filter(c => c.date.getMonth() === month.getMonth());
            if (consForMonth.length > 0) {
                return [{ equipment: equip, consumption: consForMonth.reduce((a, b) => a.date > b.date ? a : b) }];
            }

            return [{ equipment: equip, consumption: this.getAverageMonthConsumption(consumptions, month) }];
        });
    }

    // Average of an equipment's monthly records (per tariff period for electricity), dated to the given month.
    private getAverageMonthConsumption(consumptions: GroupingSimulation.IEquipmentConsumption[], month: Date): GroupingSimulation.IEquipmentConsumption {
        if (consumptions[0] instanceof GroupingSimulation.NaturalGasEquipmentConsumption) {
            return new GroupingSimulation.NaturalGasEquipmentConsumption(month, _.meanBy(consumptions, (consumption) => consumption.totalValue));
        }

        return new GroupingSimulation.ElectricityEquipmentConsumption(
            month,
            _.meanBy(consumptions, (consumption) => consumption['peak'] || 0),
            _.meanBy(consumptions, (consumption) => consumption['standard'] || 0),
            _.meanBy(consumptions, (consumption) => consumption['offPeak'] || 0),
            _.meanBy(consumptions, (consumption) => consumption['superOffPeak'] || 0)
        );
    }

    // Builds the monthly series of the simulation chart: total and protected consumption (areas), and locked, indexed and average prices (lines).
    fillEquipmentSeries(): ApexAxisChartSeries {
        const selectedYears = this.displayYearSelectionModel.selected;
        const selectedYearInterval = {
            start: startOfYear(setYear(new Date(), _.min(selectedYears)!)),
            end: endOfYear(setYear(new Date(), _.max(selectedYears)!)),
        };

        const totalConsumptionSeries = {
            name: 'TOTAL_CONSUMPTION',
            type: 'area',
            color: HedgingViewComponent.SERIES_COLORS.TOTAL_CONSUMPTION,
            data: new Array<{x: string, y: number}>()
        };

        const protectedConsumptionSeries = {
            name: 'PROTECTED_CONSUMPTION',
            type: 'area',
            color: HedgingViewComponent.SERIES_COLORS.PROTECTED_CONSUMPTION,
            data: new Array<{x: string, y: number}>()
        };

        const totalCoverPriceSeries = {
            name: 'COVER_PRICE',
            type: 'line',
            color: HedgingViewComponent.SERIES_COLORS.COVER_PRICE,
            data: new Array<{x: string, y: number | null}>()
        };

        const totalOmipPriceSeries = {
            name: 'OMIP_PRICE_PROJECTION',
            type: 'line',
            color: HedgingViewComponent.SERIES_COLORS.OMIP_PRICE_PROJECTION,
            data: new Array<{x: string, y: number | null}>()
        };

        const totalPriceSeries = {
            name: 'FINAL_PRICE',
            type: 'line',
            color: HedgingViewComponent.SERIES_COLORS.FINAL_PRICE,
            data: new Array<{x: string, y: number | null}>()
        };

        const chartMonths: { date: Date, label: string, data: ConsumptionMonthData }[] = [];

        const consumptionsByDateType: ConsumptionYearData[] = [];

        // Iterate months in selected interval
        eachMonthOfInterval(selectedYearInterval).forEach(month => {
            const monthTime = month.getTime();

            const totalConsumptionData = { x: monthTime, y: 0 };
            const coverPriceSerieData = { x: monthTime, y: 0 };
            const totalPriceSerieData = { x: monthTime, y: 0 };
            const omipPriceSerieData = { x: monthTime, y: 0 };

            const consumptionsOfMonth = this.getEstimatedMonthEquipmentConsumptions(month);

            // Price periods that apply to this month. Cover can never exceed 100% of the consumption: the contract's own covers come first
            // (capped at 100%), and the selected offers only fill what's left (scaled down if they'd go past 100%), so no MWh is counted twice.
            // The contract's part is already fixed, so it only counts for the cover %: it's left out of the costs (neither locked nor indexed).
            const offerPricesToApply = this.selectedPrices.filter(pp => isWithinInterval(month, { start: pp.deliveryStartDate, end: pp.deliveryEndDate }));
            const offerCoverSum = _.sumBy(offerPricesToApply, pp => pp.coverPercentage);
            const monthContractCover = Math.min(100, this.getContractCoverForMonth(month));
            const monthOfferCover = Math.min(100 - monthContractCover, offerCoverSum);
            const offerCoverScale = offerCoverSum > 0 ? monthOfferCover / offerCoverSum : 0;
            const monthCover = monthContractCover + monthOfferCover;
            const marketPrice = this.getMarketReferencePrice(offerPricesToApply);

            // Costs are accumulated in € (price × MWh) per equipment, then divided by the month's MWh to get €/MWh.
            // The unprotected part is priced with the indexed price estimate (OMIP adjusted by profile, losses, system costs and fee).
            let coveredCost = 0;
            let uncoveredCost = 0;
            let weightedOmip = 0;
            let weightedProfileFactor = 0;
            let weightedLossFactor = 0;

            consumptionsOfMonth.forEach(({ equipment, consumption: cons }) => {
                const typedCons = cons as GroupingSimulation.ElectricityEquipmentConsumption;
                const consumption = typedCons.totalValue;

                if (!consumption) {
                    return;
                }

                totalConsumptionData.y += consumption;

                const uncoveredConsumption = consumption * (1 - monthCover / 100);
                const profileFactor = IndexedPriceEstimate.getProfileFactor(typedCons);
                const lossFactor = IndexedPriceEstimate.getLossFactor((equipment as GroupingSimulation.ElectricityEquipment).tensionLevelDescription);

                uncoveredCost += IndexedPriceEstimate.getPrice(marketPrice, profileFactor, lossFactor) * uncoveredConsumption;
                weightedOmip += marketPrice * uncoveredConsumption;
                weightedProfileFactor += profileFactor * uncoveredConsumption;
                weightedLossFactor += lossFactor * uncoveredConsumption;
            })

            // Same calculation as the offer's base price (getLockedPriceCost), applied to this month's consumption and the price's effective cover %
            // (its cover % after the scaling above). Each price's share is kept so the year's locked cost can be shown as the sum of its covers.
            const monthLockedParts = offerPricesToApply.map(pp => {
                const lockedPriceCost = this.getLockedPriceCost(pp, consumptionsOfMonth);
                const effectiveCover = pp.coverPercentage * offerCoverScale;

                return {
                    price: pp,
                    isCapped: offerCoverScale < 1,
                    consumption: lockedPriceCost.consumption * (effectiveCover / 100),
                    cost: lockedPriceCost.cost * (effectiveCover / 100)
                };
            });
            coveredCost += _.sumBy(monthLockedParts, (part) => part.cost);

            const monthConsumption = totalConsumptionData.y;
            const coveredConsumption = monthConsumption * (monthCover / 100);
            const contractCoveredConsumption = monthConsumption * (monthContractCover / 100);
            const offerCoveredConsumption = coveredConsumption - contractCoveredConsumption;
            const monthUncoveredConsumption = monthConsumption - coveredConsumption;
            const indexedPrice = monthUncoveredConsumption > 0 ? uncoveredCost / monthUncoveredConsumption : 0;

            // Locked price is the average €/MWh of the offers' part; the indexed price estimate applies to the rest.
            // The average leaves out the contract's part, which has no cost here.
            coverPriceSerieData.y = offerCoveredConsumption > 0 ? coveredCost / offerCoveredConsumption : 0;
            omipPriceSerieData.y = monthCover < 100 ? indexedPrice : 0;
            totalPriceSerieData.y = monthConsumption - contractCoveredConsumption > 0 ? (coveredCost + uncoveredCost) / (monthConsumption - contractCoveredConsumption) : 0;

            const year = month.getFullYear();
            const quarter = getQuarter(month);
            const monthIndex = month.getMonth();

            let yearObject = consumptionsByDateType.find(y => y.year == year);
            if (!yearObject) {
                yearObject = new ConsumptionYearData(year, 0, 0, 0, 0);
                yearObject.monthsWithPricesApplied = eachMonthOfInterval(this.getMonthsWithPricesAppliedInYear(year)).map(m => m.getMonth());
                consumptionsByDateType.push(yearObject);
            }

            yearObject.totalConsumption += monthConsumption;
            yearObject.totalPriceByOMIP += uncoveredCost;
            yearObject.totalPrice += coveredCost;
            yearObject.coveredConsumption += coveredConsumption;
            yearObject.contractCoveredConsumption += contractCoveredConsumption;
            yearObject.coveredCost += coveredCost;
            yearObject.uncoveredCostEstimate += uncoveredCost;
            yearObject.indexedWeightedOmip += weightedOmip;
            yearObject.indexedWeightedProfileFactor += weightedProfileFactor;
            yearObject.indexedWeightedLossFactor += weightedLossFactor;
            yearObject.priceAverage = yearObject.lockedAveragePrice;

            monthLockedParts.forEach(({ price, isCapped, consumption, cost }) => {
                let lockedPart = yearObject!.lockedParts.find((part) => part.priceId == price.id);

                if (!lockedPart) {
                    lockedPart = new LockedCostPart(price.id, this.getFriendlyPeriodLabel(price), price.coverPercentage);
                    yearObject!.lockedParts.push(lockedPart);
                }

                lockedPart.isCapped ||= isCapped;
                lockedPart.consumption += consumption;
                lockedPart.cost += cost;
            });

            if (yearObject.monthsWithPricesApplied.includes(monthIndex)) {
                yearObject.consumptionOfMonthsWithPricesApplied += monthConsumption;
            }

            let quarterObject = yearObject.quarters.find(q => q.quarter == quarter);
            if (quarterObject) {
                quarterObject.totalConsumption += monthConsumption;
                quarterObject.totalPriceByOMIP += uncoveredCost;
            } else {
                quarterObject = new ConsumptionQuarterData(quarter, monthConsumption, uncoveredCost, 0);
                yearObject.quarters.push(quarterObject);
            }

            const monthObject = new ConsumptionMonthData(monthIndex, monthConsumption, uncoveredCost, coverPriceSerieData.y);
            monthObject.monthAsString = format(month, 'MMM', { locale: this._userService.userCountryLocale });
            monthObject.coverPercentage = monthCover;
            monthObject.contractCoverPercentage = monthContractCover;
            monthObject.coveredConsumption = coveredConsumption;
            monthObject.contractCoveredConsumption = contractCoveredConsumption;
            monthObject.coveredCost = coveredCost;
            monthObject.uncoveredCostEstimate = uncoveredCost;
            monthObject.marketPrice = marketPrice;
            monthObject.indexedPrice = indexedPrice;
            quarterObject.months.push(monthObject);

            const label = format(month, 'MMM yy', { locale: this._userService.userCountryLocale });
            chartMonths.push({ date: month, label, data: monthObject });

            // Months without a locked price / market part get null so the price lines break instead of dropping to 0
            // The simulation works in MWh and €/MWh: the series are converted to the unit the user chose
            const chartConsumptionUnit = this.energyUnitService.consumptionUnit;
            const chartPriceUnit = this.energyUnitService.priceUnit;
            const chartPrice = (price: number) => price ? EnergyUnits.convertPrice(price, chartPriceUnit) : null;

            totalConsumptionSeries.data.push({ x: label, y: EnergyUnits.convertConsumption(monthConsumption, chartConsumptionUnit) });
            protectedConsumptionSeries.data.push({ x: label, y: EnergyUnits.convertConsumption(coveredConsumption, chartConsumptionUnit) });
            totalCoverPriceSeries.data.push({ x: label, y: chartPrice(coverPriceSerieData.y) });
            totalOmipPriceSeries.data.push({ x: label, y: chartPrice(omipPriceSerieData.y) });
            totalPriceSeries.data.push({ x: label, y: chartPrice(totalPriceSerieData.y) });
        });

        this.consumptionsByDateTypes = consumptionsByDateType;
        this.simulationChartMonths = chartMonths;

        // Order must match the per-series arrays in simulationChartOptions (stroke, fill opacity, marker size) and the y-axis seriesName lists
        return [totalConsumptionSeries, protectedConsumptionSeries, totalCoverPriceSeries, totalOmipPriceSeries, totalPriceSeries];
    };

    // Recalculates the simulation and pushes the new series (and year separators) to the chart.
    refreshSimulationCharts() {
        this.simulationChartOptions.series = this.fillEquipmentSeries();
        this.simulationChartOptions.annotations = { xaxis: this.getYearSeparatorAnnotations() };
    }

    // Dashed line + year label at each January after the first, so multi-year charts are easy to split by year.
    private getYearSeparatorAnnotations(): XAxisAnnotations[] {
        return this.simulationChartMonths
            .filter((month, index) => index > 0 && month.date.getMonth() == 0)
            .map((month) => ({
                x: month.label,
                borderColor: '#d8dae3',
                strokeDashArray: 4,
                label: {
                    text: month.date.getFullYear().toString(),
                    orientation: 'horizontal',
                    position: 'top',
                    textAnchor: 'start',
                    offsetY: -4,
                    borderWidth: 0,
                    style: {
                        color: '#7e8299',
                        background: 'transparent',
                        fontSize: '11px',
                        fontWeight: 600
                    }
                }
            }));
    }

    // Shows every month for a single year; for longer ranges only every 3rd month, and the year only on January (and the first month).
    private formatSimulationMonthLabel(label: string): string {
        const index = this.simulationChartMonths.findIndex((month) => month.label == label);
        const month = this.simulationChartMonths[index];

        if (!month) {
            return label ?? '';
        }

        const monthIndex = month.date.getMonth();
        const step = this.simulationChartMonths.length <= 12 ? 1 : this.simulationChartMonths.length <= 36 ? 3 : 6;

        if (monthIndex % step != 0) {
            return '';
        }

        const locale = this._userService.userCountryLocale;
        return format(month.date, monthIndex == 0 || index == 0 ? 'MMM yy' : 'MMM', { locale });
    }

    private getSimulationChartLegend(): ApexLegend {
        return {
            position: 'top',
            horizontalAlign: 'left',
            fontSize: '12px',
            offsetY: 4,
            itemMargin: { horizontal: 10, vertical: 2 },
            formatter: (legendName) => this.translateWithUnit(this.translationStream.value?.[legendName] ?? legendName)
        };
    }

    private getSimulationChartGrid(): ApexGrid {
        return {
            borderColor: '#eff2f5',
            strokeDashArray: 3,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            padding: { left: 8, right: 8 }
        };
    }

    private getSimulationChartXAxis(): ApexXAxis {
        return {
            type: 'category',
            axisBorder: { show: true, color: '#e4e6ef' },
            axisTicks: { show: false },
            tooltip: { enabled: false },
            labels: {
                rotate: 0,
                hideOverlappingLabels: false,
                trim: false,
                style: { colors: '#a1a5b7', fontSize: '11px' },
                formatter: (label: string) => this.formatSimulationMonthLabel(label)
            }
        };
    }

    // Consumption (MWh) on the left for the areas, price (€/MWh) on the right for the lines.
    private getSimulationChartYAxes(): ApexYAxis[] {
        const translations = this.translationStream.value ?? {};
        const axis = (seriesName: string[], title: string, opposite: boolean, maximumFractionDigits: number): ApexYAxis => ({
            seriesName,
            opposite,
            min: 0,
            forceNiceScale: true,
            showForNullSeries: false,
            title: {
                text: this.translateWithUnit(translations[title]),
                style: { color: '#7e8299', fontSize: '11px', fontWeight: 500 }
            },
            labels: {
                style: { colors: '#a1a5b7', fontSize: '11px' },
                formatter: (value: number) => value == null ? '' : value.toLocaleString(this._userService.userCountryLocale?.code ?? 'pt-PT', { maximumFractionDigits })
            }
        });

        return [
            // Consumptions keep up to 3 decimals of MWh (whole kWh); prices are whole €/MWh, or €/kWh with 4 decimals
            axis(['TOTAL_CONSUMPTION', 'PROTECTED_CONSUMPTION'], 'CHART_CONSUMPTION_AXIS', false, this.energyUnitService.consumptionUnit == 'kWh' ? 0 : 3),
            axis(['COVER_PRICE', 'OMIP_PRICE_PROJECTION', 'FINAL_PRICE'], 'CHART_PRICE_AXIS', true, this.energyUnitService.priceUnit == 'kWh' ? 4 : 0)
        ];
    }

    private getSimulationChartTooltip(): ApexTooltip {
        return {
            shared: true,
            intersect: false,
            followCursor: false,
            custom: (options: any) => CustomChartTooltips.getCoverSimulationChart(
                this.simulationChartMonths[options.dataPointIndex], this.translationStream.value ?? {}, this._userService.userCountryLocale,
                { consumption: this.energyUnitService.consumptionUnit, price: this.energyUnitService.priceUnit }
            )
        };
    }

    // Returns the market (OMIP) €/MWh used to estimate the cost of the unprotected consumption of a month.
    // Uses the shortest applied price period's OMIP product since it's the most specific, falling back to the next year's product.
    getMarketReferencePrice(pricesToApply: PricePeriods.ElectricityPrices[]): number {
        const mostSpecificPrice = _.minBy(
            pricesToApply.filter(pp => (pp.omipElectricityHistory?.price_MW ?? 0) > 0),
            pp => pp.deliveryEndDate.getTime() - pp.deliveryStartDate.getTime()
        );

        return mostSpecificPrice?.omipElectricityHistory?.price_MW ?? this.omipIntervalPriceData._2_year?.lastPrice_MW ?? 0;
    }

    // Years with unprotected consumption priced by the indexed price estimate (shown with the formula in the result step).
    get yearsWithIndexedConsumption(): ConsumptionYearData[] {
        return this.consumptionsByDateTypes.filter(yearData => yearData.uncoveredConsumption > 0 && yearData.averageIndexedPrice > 0);
    }

    // Opens the explanation of the indexed price formula, with each factor and a single calculation.
    // The indexed price is the same for every year, so the values are weighted by the unprotected consumption of all years.
    openIndexedPriceFormulaInfo() {
        const years = this.yearsWithIndexedConsumption;
        const uncoveredConsumption = _.sumBy(years, yearData => yearData.uncoveredConsumption);
        const weightedAverage = (getValue: (yearData: ConsumptionYearData) => number) =>
            uncoveredConsumption > 0 ? _.sumBy(years, yearData => getValue(yearData) * yearData.uncoveredConsumption) / uncoveredConsumption : 0;

        this._dialog.open(IndexedPriceFormulaInfoComponent, {
            width: '640px',
            maxHeight: '90vh',
            hasBackdrop: true,
            data: {
                formulaValues: uncoveredConsumption > 0 ? {
                    averageOmip: weightedAverage(yearData => yearData.averageOmip),
                    averageProfileFactor: weightedAverage(yearData => yearData.averageProfileFactor),
                    averageLossFactor: weightedAverage(yearData => yearData.averageLossFactor),
                    averageIndexedPrice: weightedAverage(yearData => yearData.averageIndexedPrice)
                } : undefined
            }
        });
    }

    // Sums the cover % of the selected prices (and the contract's own covers) that apply to a month, optionally ignoring one price (used while editing it).
    getCoverForMonth(month: Date, excludedPriceId?: string): number {
        return this.getContractCoverForMonth(month) + _.sumBy(
            this.pricePeriodSelectionModel.selected.filter(pp =>
                pp.coverPercentage > 0 && pp.id != excludedPriceId &&
                isWithinInterval(month, { start: pp.deliveryStartDate, end: pp.deliveryEndDate })
            ),
            pp => pp.coverPercentage
        );
    }

    // Cover % given by the contract's own covers in a month.
    getContractCoverForMonth(month: Date): number {
        return _.sumBy(
            this.contractCovers.filter(pp => isWithinInterval(month, { start: pp.deliveryStartDate, end: pp.deliveryEndDate })),
            pp => pp.coverPercentage
        );
    }

    // Cover % for each of the 12 months of a year (total and the contract's part), capped at 100. Cached like getSelectedPricesContainerDataByYear.
    getCoverByMonth(year: number): { total: number, contract: number }[] {
        let coverByMonth = this._coverByMonthCache.get(year);

        if (!coverByMonth) {
            coverByMonth = eachMonthOfInterval({ start: startOfYear(setYear(new Date(), year)), end: endOfYear(setYear(new Date(), year)) })
                .map(month => ({ total: Math.min(100, this.getCoverForMonth(month)), contract: Math.min(100, this.getContractCoverForMonth(month)) }));
            this._coverByMonthCache.set(year, coverByMonth);
        }

        return coverByMonth;
    }

    // A year can't be hidden while it has locked prices (same rule enforced by toggleYear).
    isYearLocked(year: number): boolean {
        const pricesInterval = this.selectedPricesTotalInterval;
        return !!pricesInterval && isWithinInterval(startOfYear(setYear(new Date(), year)), pricesInterval);
    }

    // Turns a delivery period into a plain-language label, e.g. "Ano completo 2027" or "2.º trimestre 2026".
    getFriendlyPeriodLabel(period: { deliveryStartDate: Date, deliveryEndDate: Date }): string {
        const { deliveryStartDate: start, deliveryEndDate: end } = period;
        const locale = this._userService.userCountryLocale;

        if (isSameDay(start, startOfYear(start)) && isSameDay(end, endOfYear(start))) {
            return this._translationService.instant('PERIOD_LABEL_YEAR', { year: getYear(start) });
        }
        if (isSameDay(start, startOfQuarter(start)) && isSameDay(end, endOfQuarter(start))) {
            return this._translationService.instant('PERIOD_LABEL_QUARTER', { quarter: getQuarter(start), year: getYear(start) });
        }
        if (isSameDay(start, startOfMonth(start)) && isSameDay(end, endOfMonth(start))) {
            return _.upperFirst(format(start, 'MMMM yyyy', { locale }));
        }

        return `${format(start, 'MMM yy', { locale })} – ${format(end, 'MMM yy', { locale })}`;
    }

    // Difference (%) between a supplier price and the market (OMIP) price for the same period, or null when there's no market reference.
    getMarketComparison(price: PricePeriods.ElectricityPrices): number | null {
        const marketPrice = price.omipElectricityHistory?.price_MW;

        if (!marketPrice || !price.basePrice) {
            return null;
        }

        return (price.basePrice - marketPrice) / marketPrice * 100;
    }

    // Translation key + absolute percentage describing how a supplier price compares to the market.
    getMarketComparisonLabel(price: PricePeriods.ElectricityPrices): { key: string, percentage: number } | null {
        const comparison = this.getMarketComparison(price);

        if (comparison == null) {
            return null;
        }
        if (Math.abs(comparison) < 1) {
            return { key: 'MARKET_COMPARISON_EQUAL', percentage: 0 };
        }

        return { key: comparison > 0 ? 'MARKET_COMPARISON_ABOVE' : 'MARKET_COMPARISON_BELOW', percentage: Math.abs(comparison) };
    }

    // Price variation of an OMIP product over the last month, based on its history.
    private calculateOmipTrend(omipPrice: IElectricityOmipPricesPT): OmipTrend | null {
        const history = _.sortBy(omipPrice.electricityOmipPricesHistory, (h) => h.priceDate.getTime());
        const latest = _.last(history);

        if (!latest) {
            return null;
        }

        const oneMonthBefore = subMonths(latest.priceDate, 1).getTime();
        const reference = _.minBy(history, (h) => Math.abs(h.priceDate.getTime() - oneMonthBefore));

        if (!reference || reference === latest || !reference.price_MW) {
            return null;
        }

        const percentage = (latest.price_MW - reference.price_MW) / reference.price_MW * 100;

        return {
            percentage,
            direction: Math.abs(percentage) < 1 ? 'stable' : (percentage > 0 ? 'up' : 'down')
        };
    }

    private readMarketStepSeen(): boolean {
        try {
            return localStorage.getItem(HedgingViewComponent.MARKET_STEP_SEEN_STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    }

    private writeMarketStepSeen() {
        try {
            localStorage.setItem(HedgingViewComponent.MARKET_STEP_SEEN_STORAGE_KEY, 'true');
        } catch {
            // Storage unavailable (private mode, blocked site data): the step simply opens again next time
        }
    }

    // Monitors drag movement and highlights the year container that the period is being dropped into.
    dragMove(dragMoveEvent: CdkDragMove) {
        let draggedPrice: PricePeriods.ElectricityPrices = dragMoveEvent.source.data;
        let draggedPriceContainer: HTMLElement = dragMoveEvent.source.element.nativeElement;
        let event: MouseEvent = (dragMoveEvent.event as MouseEvent)
        let dragX = event.clientX,
            dragY = event.clientY;

        this.dropContainersBoundingBoxes.forEach( (divElement, year) => {
            let rectBound = divElement.getBoundingClientRect();
            
            if (dragX > rectBound.left && dragX < rectBound.right && dragY > rectBound.top && dragY < rectBound.bottom && (draggedPrice.deliveryStartDate.getFullYear() == +year || draggedPrice.deliveryEndDate.getFullYear() == +year)) {
                let newSimulation: PricePeriodsSimulation = {
                    year: +year,
                    price: draggedPrice,
                    priceElement: draggedPriceContainer,
                    targetContainer: divElement
                }

                this.dragInYearFlag$.next(newSimulation);

            } else if (this.dragInYearFlag$.getValue()?.year == +year) {
                this.dragInYearFlag$.next(undefined);
            }
        })
    }

    // Finalizes a drag operation and opens the cover dialog when a valid year target was selected.
    dragDrop(dragDropEvent: CdkDragEnd) {
        let simulation = this.dragInYearFlag$.getValue()
        if (simulation) {
            this.removeSimulatedDiv(simulation);
            this.openCoverDialog(dragDropEvent.source.data);
        }

        dragDropEvent.source.reset();
        
    }

    // Opens the dialog used to define the cover percentage for a given price period.
    openCoverDialog(price: PricePeriods.ElectricityPrices) {
        let pricePeriodInterval = {start: price.deliveryStartDate, end: price.deliveryEndDate}
        let monthsOfInterval = eachMonthOfInterval(pricePeriodInterval).map((month) => ({
            monthAsDate: month,
            cover: this.getCoverForMonth(month, price.id),
            consumption: _.sumBy(this.getEstimatedMonthConsumptions(month), (cons) => cons.totalValue)
        }));

        this._dialog.open(CoverDialogComponent, {
            width: '560px',
            maxHeight: '90vh',
            hasBackdrop: true,
            disableClose: true,
            data: {
                pricePeriod: price,
                periodLabel: this.getFriendlyPeriodLabel(price),
                coversByMonth: monthsOfInterval,
                locale: this._userService.userCountryLocale
            }
        }).afterClosed().subscribe((coverPercentage: number = 0) => {
            if (coverPercentage > 0) {
                price.coverPercentage = coverPercentage;

                if (!this.pricePeriodIsSelected(price)) {
                    this.pricePeriodSelectionModel.select(price);
                } else {
                    this.pricePeriodCoverChangeFlag$.next();
                }
            } else if (coverPercentage == 0 && this.pricePeriodIsSelected(price)) {
                this.pricePeriodSelectionModel.deselect(price);
            }
        })
    }

    // getSelectedPricesContainerDataByYear builds new objects on every change detection; tracking by id keeps the DOM
    // (and the remove button under the cursor) alive, otherwise clicks are lost while the elements are recreated.
    trackByPriceContainer(_index: number, priceContainer: PriceContainer): string {
        return priceContainer.price.id;
    }

    trackByYear(_index: number, yearData: ConsumptionYearData): number {
        return yearData.year;
    }

    // Tooltip of the year's locked cost: each cover's MWh × its price over this year's months, which add up to the locked cost.
    getLockedCostBreakdownTooltip(yearData: ConsumptionYearData): string {
        const locale = this._userService.userCountryLocale?.code ?? 'pt-PT';
        const formatNumber = (value: number, minDecimals: number, maxDecimals: number) =>
            value.toLocaleString(locale, { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals });

        const lines = yearData.lockedParts.map((part) =>
            `${part.isCapped ? '⚠ ' : ''}${part.label}: ` +
            `${formatNumber(part.cost, 0, 0)} €`
        );

        if (yearData.hasCappedLockedPart) {
            lines.push('', '⚠ ' + this._translationService.instant('COVER_CAPPED_TOOLTIP'));
        }

        return [this._translationService.instant('LOCKED_COST_BREAKDOWN'), ...lines].join('\n');
    }

    // Removes the cover from a price period and clears it from the active selection.
    removePriceCover(price: PricePeriods.ElectricityPrices) {
        // The block is removed, so mouseleave never fires on its remove button
        this.hoveredRemovePriceId = null;
        price.coverPercentage = 0;
        this.pricePeriodSelectionModel.deselect(price);
    }

    // Switches the screen between the cover and consumption visualizations.
    changeDisplayMode(displayMode: 'Covers' | 'Consumptions') {
        this.newDisplayMode = displayMode;

        if (displayMode == 'Consumptions') {
            this.refreshSimulationCharts();
        }
    }

    // Creates the temporary visual placeholder showing where a dragged price period would be placed in a year.
    createSimulatedDiv(priceSimulation: PricePeriodsSimulation) {
        const start = priceSimulation.price?.deliveryStartDate;
        const end = priceSimulation.price?.deliveryEndDate;
        const priceContainer = priceSimulation.priceElement;

        if (!start || !end || !priceContainer) {
            return;
        }

        const descriptionText = priceSimulation.price ? this.getFriendlyPeriodLabel(priceSimulation.price) : '';

        this.dropContainersBoundingBoxes.forEach((yearContainer, year) => {
            const targetContainer = yearContainer.getElementsByClassName('price-period-container').item(0) as HTMLElement | null;

            if (!targetContainer) {
                return;
            }

            const existingPlaceholder = targetContainer.querySelector('.drag-placeholder') as HTMLElement | null;
            if (existingPlaceholder) {
                this._renderer2.removeChild(targetContainer, existingPlaceholder);
            }

            const yearInterval = {
                start: startOfYear(setYear(new Date(), +year)),
                end: endOfYear(setYear(new Date(), +year))
            };

            if (!areIntervalsOverlapping({ start, end }, yearInterval)) {
                return;
            }

            const clippedStart = start < yearInterval.start ? yearInterval.start : start;
            const clippedEnd = end > yearInterval.end ? yearInterval.end : end;
            const columnStart = clippedStart.getMonth() + 1;
            const columnEnd = clippedEnd.getMonth() + 2;

            const yearClass = `${start.getFullYear() === +year ? 'start-year' : ''}${end.getFullYear() === +year ? 'end-year' : ''}`;

            const placeholder = priceContainer.cloneNode(true) as HTMLElement;
            placeholder.classList.add('drag-placeholder');
            placeholder.className = `position-relative border border-1 border-solid p-2 fs-8 text-muted bg-secondary border-dark-clarity drag-placeholder ${yearClass}`;
            this._renderer2.setStyle(placeholder, 'grid-column', `${columnStart} / ${columnEnd}`);
            this._renderer2.setStyle(placeholder, 'grid-row', '1 / 2');
            this._renderer2.setStyle(placeholder, 'opacity', '0.6');
            this._renderer2.removeStyle(placeholder, 'top');
            this._renderer2.removeStyle(placeholder, 'left');
            this._renderer2.removeStyle(placeholder, 'position');

            const removeCoverButton = placeholder.querySelector('.remove-cover-button') as HTMLElement | null;
            if (removeCoverButton) {
                removeCoverButton.remove();
            }

            placeholder.querySelectorAll('button, .fa-grip-vertical').forEach((element) => element.remove());

            const contentBlock = placeholder.querySelector('.mt-5') as HTMLElement | null;
            if (contentBlock) {
                contentBlock.classList.remove('mt-5');
            }

            const titleNodes = placeholder.querySelectorAll('div');
            if (titleNodes.length > 0) {
                titleNodes[0].textContent = descriptionText;
            }

            this._renderer2.appendChild(targetContainer, placeholder);
        });
    }

    // Removes the temporary drag placeholder from the target year container.
    removeSimulatedDiv(priceSimulation: PricePeriodsSimulation) {
        this.dropContainersBoundingBoxes.forEach((yearContainer) => {
            const targetContainer = yearContainer.getElementsByClassName('price-period-container').item(0) as HTMLElement | null;
            const placeholder = targetContainer?.querySelector('.drag-placeholder') as HTMLElement | null;

            if (placeholder && targetContainer) {
                this._renderer2.removeChild(targetContainer, placeholder);
            }
        });
    }

    // Calculates the average annual cover percentage across the selected price interval.
    get intervalYearlyCoverData() {
        let pricesInterval = this.selectedPricesTotalInterval;

        if (!pricesInterval) {
            return 0;
        }

        let yearsOfInterval = []
        let monthsOfInterval = eachMonthOfInterval(pricesInterval).map((month) => ({ monthAsDate: month, cover: 0 }));

        monthsOfInterval.forEach( (month, index, originalArray) => {
            month.cover = this.pricePeriodSelectionModel.selected.filter( pp => 
                    pp.coverPercentage > 0 && 
                    isWithinInterval(month.monthAsDate, {start: pp.deliveryStartDate, end: pp.deliveryEndDate})
                ).reduce((sum, pp) => {return sum += pp.coverPercentage}, 0);

            if (getMonth(month.monthAsDate) == 11) {
                let yearlyCoverPercentage = _.sumBy(originalArray.slice(index - 11, index), (arr) => arr.cover) / 12;

                yearsOfInterval.push({year: yearlyCoverPercentage})
            }
        })

        return _.sumBy(monthsOfInterval, (m) => m.cover) / 12
    }

    // Refreshes selection state whenever the input data or market changes.
    ngOnChanges(changes: SimpleChanges) {
        let opportunityChanges = changes['selectedOpportunity'];
        let contractChanges = changes['contractId'];
        let groupingChanges = changes['selectedGrouping'];
        let marketChanges = changes['selectedMarket'];

        if ((opportunityChanges && opportunityChanges.currentValue != opportunityChanges.previousValue)
            || (contractChanges && contractChanges.currentValue != contractChanges.previousValue)) {
            this.currentEquipments = [];

            this.pricePeriodSelectionModel.clear();
        }

        if (groupingChanges && groupingChanges.currentValue != groupingChanges.previousValue) {
            this.pricePeriodSelectionModel.clear();
        }

        if (marketChanges && marketChanges.currentValue != marketChanges.previousValue) {
            this.pricePeriodSelectionModel.clear();
        }

        // A picked price already narrows the offers to its own conditions
        if (changes['selectedContract'] && this.pricePeriodSelectionModel.selected.length == 0) {
            this.resetEconomicConditionsFilter();
        }

        if (changes['selectedContract']) {
            this.loadContractEconomicConditions();
        }

        if (changes['currentEquipments'] || contractChanges) {
            this.loadContractCovers();
        }
    }

    // On a contract the offers are only the ones whose electricity economic condition is active for the contract's
    // product, general and particular conditions. The details of the ones that have prices (lines or versions) are also loaded.
    loadContractEconomicConditions() {
        this._contractEconomicConditionsSubscription?.unsubscribe();
        this.contractElecEconomicConditionIds = null;
        this.contractElecEconomicConditionsWithPrices = [];

        let productId = this.selectedContract?.supplierProduct?.id;
        let generalConditionId = this.selectedContract?.productGeneralCondition?.id;
        let particularConditionId = this.selectedContract?.productParticularCondition?.id;

        if (productId == null || generalConditionId == null || particularConditionId == null) {
            return;
        }

        this._contractEconomicConditionsSubscription = this._economicConditionsService
            .getActiveElectricityEconomicConditions(productId, generalConditionId, particularConditionId)
            .pipe(
                switchMap((sr) => {
                    const elecEconomicConditions = sr.data ?? [];
                    // The offers can already be filtered while the details are loading
                    this.contractElecEconomicConditionIds = elecEconomicConditions.map((ec) => ec.id);

                    // One request per economic condition with prices; a failed one only leaves that condition out
                    const detailRequests = elecEconomicConditions
                        .filter((ec) => ec.configurations.hasPriceLines || ec.configurations.hasPriceVersions)
                        .map((ec) => this._economicConditionsService.getProductElectricityEconomicCondition(ec.id).pipe(
                            map((detailSr) => detailSr.data),
                            catchError(() => of(null))
                        ));

                    return detailRequests.length > 0 ? forkJoin(detailRequests) : of([]);
                }),
                takeUntil(this._destroy$)
            )
            .subscribe({
                next: (details) => this.contractElecEconomicConditionsWithPrices = details.filter((detail) => detail != null),
                error: () => this.contractElecEconomicConditionIds = []
            });
    }

    // Without selected prices the offers follow the selected contract's conditions (if any); otherwise every offer is shown.
    // The contract doesn't carry an electricity economic condition, so that one stays open until a price is picked.
    resetEconomicConditionsFilter() {
        this.economicConditionsFilterForm.setValue({
            spGeneralConditionId: this.selectedContract?.productGeneralCondition?.id ?? null,
            spElectricityEconomicConditionId: null,
            spParticularConditionId: this.selectedContract?.productParticularCondition?.id ?? null,
            spNaturalGasEconomicConditionId: null,
            supplierProductId: this.selectedContract?.supplierProduct?.id ?? null
        });
    }

    // When simulating on a contract, the selected electricity meters' guid is their supply contract id (originId):
    // loads each one's economic conditions and turns the fixed ones into locked covers.
    loadContractCovers() {
        const supplyContractIds = String.isNullOrUndefinedOrWhitespace(this.contractId) || this.selectedMarket != 'Electricity'
            ? []
            : _.uniq(this.currentEquipments.map( (equipment) => equipment.guid ).filter( (guid): guid is string => !String.isNullOrUndefinedOrWhitespace(guid) ));
        const coversKey = _.sortBy(supplyContractIds).join('|');

        if (coversKey == this._contractCoversKey) {
            return;
        }

        this._contractCoversKey = coversKey;
        this._contractCoversSubscription?.unsubscribe();
        this.contractCovers = [];

        if (supplyContractIds.length == 0) {
            this.refreshSimulationChartsWithEquipments();
            return;
        }

        this._contractCoversSubscription = forkJoin(supplyContractIds.map( (supplyContractId) =>
            this._contractsService.getElectricitySupplyContractEconomicConditions(supplyContractId).pipe(
                map( (sr) => sr.data ?? [] ),
                catchError( () => of([] as ElectricitySupplyContractEconomicCondition[]) )
            )
        )).pipe(
            map( (conditionsBySupply) => conditionsBySupply.flat() )
        ).subscribe( (conditions) => {
            this.contractCovers = this.mapContractCovers(conditions);

            // Shows the years the contract covers (within the years the chart can display)
            const coveredYears = this.availableYears.filter( (year) => this.contractCovers.some( (cover) =>
                areIntervalsOverlapping(
                    { start: cover.deliveryStartDate, end: cover.deliveryEndDate },
                    { start: startOfYear(setYear(new Date(), year)), end: endOfYear(setYear(new Date(), year)) }
                )
            ));

            if (coveredYears.length > 0) {
                this.displayYearSelectionModel.select(...coveredYears);
            }

            // The contract's covers count for the cover % shown on the board and results
            this.refreshSimulationChartsWithEquipments();
        });
    }

    // A fixed economic condition locks its fixed percentage of the consumption at the fixed total price (€/MWh)
    // between the condition's start and end dates. Without an end date it lasts until the last year the chart can show.
    private mapContractCovers(conditions: ElectricitySupplyContractEconomicCondition[]): PricePeriods.ElectricityPrices[] {
        const lastChartDate = endOfYear(setYear(new Date(), _.max(this.availableYears)!));

        const covers = conditions.flatMap( (condition) => {
            const fixed = condition.fixedEconomicCondition;
            const fixedPrice = fixed?.totalOperatingExpense_MWh ?? 0;
            // Without a percentage the fixed price covers all the consumption, unless part of the contract is indexed
            const coverPercentage = fixed?.fixedPercentage ?? (condition.indexedEconomicCondition ? 0 : 100);
            const startDate = condition.startDate;
            const endDate = condition.endDate ?? lastChartDate;

            if (!fixed || fixedPrice <= 0 || coverPercentage <= 0 || !startDate) {
                return [];
            }

            // Same €/kWh in every tariff period, so getLockedPriceCost gives back the fixed €/MWh for any tariff cycle
            const pricePerKWh = fixedPrice / 1000;
            const cover = new PricePeriods.ElectricityPrices(endDate, startDate, this.contractId!.toString(), this.contractId!.toString(), 'contract-' + condition.id, pricePerKWh, pricePerKWh, pricePerKWh, pricePerKWh);
            cover.basePrice = fixedPrice;
            cover.coverPercentage = Math.min(100, coverPercentage);

            return [cover];
        });

        // The supplies of a contract usually share the same conditions: keep one cover per period, price and percentage
        return _.uniqBy(covers, (cover) => [cover.deliveryStartDate.getTime(), cover.deliveryEndDate.getTime(), cover.basePrice, cover.coverPercentage].join('|'));
    }

    private refreshSimulationChartsWithEquipments() {
        if (this.currentEquipments.length > 0) {
            this.refreshSimulationCharts();
        }
    }

    // Sets each offer's base price (€/MWh) as the average of its period prices (€/kWh) weighted by the consumption it would actually apply to:
    // the selected equipments' estimated consumption in the months of the offer's delivery period (same estimate as the simulation).
    // Each equipment is priced with its own tariff (simples / bi-horário / tri-horário / four periods) and weighted by its MWh,
    // so larger consumers weigh more. When there's no data for those months, the equipments' average monthly consumption is used.
    recalculatePriceHistoryBasePrices() {
        if (this.selectedMarket == 'Electricity') {
            const electricityEquipments = (this.currentEquipments as GroupingSimulation.ElectricityEquipment[])
                .filter((equipment) => (equipment.consumptions?.length ?? 0) > 0);

            if (electricityEquipments.length == 0) {
                return;
            }

            this.currentElectricityPrices.forEach((price) => {
                const lockedPriceCost = this.getBasePriceCost(price, electricityEquipments).cost;

                price.basePrice = lockedPriceCost.consumption > 0 ? lockedPriceCost.cost / lockedPriceCost.consumption : 0;
            });
        } else {
            // let gasEquipments = this.currentEquipments as GroupingSimulation.NaturalGasEquipment[];
            // if (gasEquipments.length > 0) {

            //     this.currentPricePeriods.forEach( (price) => {
            //         price.forEach( (hist) => {
            //             let weightedAveragePricePerKWh = hist.value;
            //             hist.basePrice = weightedAveragePricePerKWh * 1000;
            //         })    
            //     })
            // }
        }
    }

    // Consumptions an offer's base price is weighted by: the equipments' estimated consumption over the offer's delivery period,
    // or their average monthly consumption (over all their records) when there's no data for those months.
    private getBasePriceCost(price: PricePeriods.ElectricityPrices, electricityEquipments: GroupingSimulation.ElectricityEquipment[]): {
        cost: { cost: number, consumption: number },
        equipmentConsumptions: { equipment: GroupingSimulation.IEquipment, consumption: GroupingSimulation.IEquipmentConsumption | ElectricityPriceWeighting.PeriodValues }[],
        usesAverageConsumption: boolean
    } {
        const deliveryPeriodConsumptions = this.getDeliveryPeriodEquipmentConsumptions(price);
        const deliveryPeriodCost = this.getLockedPriceCost(price, deliveryPeriodConsumptions);

        if (deliveryPeriodCost.consumption > 0) {
            return { cost: deliveryPeriodCost, equipmentConsumptions: deliveryPeriodConsumptions, usesAverageConsumption: false };
        }

        const averageEquipmentConsumptions = electricityEquipments.map((equipment) => ({
            equipment,
            consumption: {
                peak: _.meanBy(equipment.consumptions, (consumption) => consumption.peak || 0),
                standard: _.meanBy(equipment.consumptions, (consumption) => consumption.standard || 0),
                offPeak: _.meanBy(equipment.consumptions, (consumption) => consumption.offPeak || 0),
                superOffPeak: _.meanBy(equipment.consumptions, (consumption) => consumption.superOffPeak || 0)
            }
        }));

        return { cost: this.getLockedPriceCost(price, averageEquipmentConsumptions), equipmentConsumptions: averageEquipmentConsumptions, usesAverageConsumption: true };
    }

    // Opens the step by step calculation of an offer's base price: the offer's prices, each equipment's consumption and tariff, and how they add up.
    openBasePriceBreakdown(price: PricePeriods.ElectricityPrices) {
        const electricityEquipments = (this.currentEquipments as GroupingSimulation.ElectricityEquipment[])
            .filter((equipment) => (equipment.consumptions?.length ?? 0) > 0);

        if (this.selectedMarket != 'Electricity' || electricityEquipments.length == 0) {
            return;
        }

        const { equipmentConsumptions, usesAverageConsumption } = this.getBasePriceCost(price, electricityEquipments);

        // The months within the offer's delivery period, estimated like the chart: the equipment's latest record for that month, or its average month
        const periodStart = new Date(price.deliveryStartDate);
        const periodEnd = new Date(price.deliveryEndDate);
        const deliveryMonths = isNaN(periodStart.getTime()) || isNaN(periodEnd.getTime()) || periodStart > periodEnd
            ? []
            : eachMonthOfInterval({ start: periodStart, end: periodEnd })
                .map((month) => ({ month, entries: this.getEstimatedMonthEquipmentConsumptions(month) }));

        // An equipment has one entry per month of the delivery period: sum them to get its consumption in each tariff period
        const equipments = Object.values(_.groupBy(equipmentConsumptions, ({ equipment }) => equipment.code)).map((entries) => {
            const periods = entries.map(({ consumption }) => consumption as ElectricityPriceWeighting.PeriodValues);

            return {
                code: entries[0].equipment.code,
                tariffCycle: ElectricityPriceWeighting.getTariffCycle(entries[0].equipment as GroupingSimulation.ElectricityEquipment),
                consumption: {
                    peak: _.sumBy(periods, (period) => Math.max(0, period.peak || 0)),
                    standard: _.sumBy(periods, (period) => Math.max(0, period.standard || 0)),
                    offPeak: _.sumBy(periods, (period) => Math.max(0, period.offPeak || 0)),
                    superOffPeak: _.sumBy(periods, (period) => Math.max(0, period.superOffPeak || 0))
                }
            };
        });

        this._dialog.open(BasePriceBreakdownComponent, {
            width: '1100px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            hasBackdrop: true,
            data: {
                periodLabel: this.getFriendlyPeriodLabel(price),
                description: price.description,
                deliveryStartDate: price.deliveryStartDate,
                deliveryEndDate: price.deliveryEndDate,
                usesAverageConsumption,
                recentConsumptions: equipments.map((equipment) => ({
                    code: equipment.code,
                    tariffCycle: equipment.tariffCycle,
                    months: deliveryMonths.flatMap(({ month, entries }) => {
                        const consumption = entries.find((entry) => entry.equipment.code == equipment.code)?.consumption;
                        const hasRecord = electricityEquipments.find((electricityEquipment) => electricityEquipment.code == equipment.code)
                            ?.consumptions.some((record) => record.totalValue > 0 && record.date.getMonth() === month.getMonth());

                        return consumption ? [{ month, consumption, estimated: !hasRecord }] : [];
                    })
                })),
                offerPrices: { peak: price.peak, standard: price.standard, offPeak: price.offPeak, superOffPeak: price.superOffPeak },
                breakdown: ElectricityPriceWeighting.getPriceBreakdown(price, equipments)
            }
        });
    }

    // Estimated consumption of each equipment for every month of the offer's delivery period.
    private getDeliveryPeriodEquipmentConsumptions(price: PricePeriods.ElectricityPrices): { equipment: GroupingSimulation.IEquipment, consumption: GroupingSimulation.IEquipmentConsumption }[] {
        const start = new Date(price.deliveryStartDate);
        const end = new Date(price.deliveryEndDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
            return [];
        }

        return eachMonthOfInterval({ start, end }).flatMap((month) => this.getEstimatedMonthEquipmentConsumptions(month));
    }

    // Cost (€) of buying the given consumptions at a supplier price, each equipment with its own tariff, and the MWh it covers.
    // Shared by the offer's base price (cost / consumption over its delivery period) and the simulation's locked cost (cost × cover % per month),
    // so both always follow the same formula.
    getLockedPriceCost(
        price: PricePeriods.ElectricityPrices,
        equipmentConsumptions: { equipment: GroupingSimulation.IEquipment, consumption: GroupingSimulation.IEquipmentConsumption | ElectricityPriceWeighting.PeriodValues }[]
    ): { cost: number, consumption: number } {
        let cost = 0;
        let totalConsumption = 0;

        equipmentConsumptions.forEach(({ equipment, consumption }) => {
            const periods = consumption as ElectricityPriceWeighting.PeriodValues;
            const total = (periods.peak || 0) + (periods.standard || 0) + (periods.offPeak || 0) + (periods.superOffPeak || 0);

            if (total <= 0) {
                return;
            }

            const tariffCycle = ElectricityPriceWeighting.getTariffCycle(equipment as GroupingSimulation.ElectricityEquipment);
            cost += ElectricityPriceWeighting.getWeightedPricePerMWh(price, periods, tariffCycle) * total;
            totalConsumption += total;
        });

        return { cost, consumption: totalConsumption };
    }

    // Returns the latest OMIP price entry for a specific price period.
    getCurrentOmipPriceByPriceId(priceId: string) {
        let price = this.currentElectricityPrices.find( (price) => price.id == priceId );
        
        if (this.selectedMarket == 'Electricity') {
            if (price && price.omipElectricityHistory) {
                return price.omipElectricityHistory;
            }  
            else {
                return null;
            }
        } else {
            // if (price && price.omipNaturalGasHistory.length > 0) {
            //     return _.minBy(price.omipNaturalGasHistory, (history) => Math.abs(new Date().getTime() - new Date(history.priceDate).getTime()) ) ?? null;
            // }  
            // else {
                return null;
            // }
        }
    }

    // Returns the price periods currently selected by the user.
    get selectedPrices() {
        return this.currentElectricityPrices.filter( (price) => this.pricePeriodIsSelected(price) );
    }

    // Checks whether every period in a date bucket is selected.
    pricePeriodByMarketTypeAndDateTypeIsSelected( dateType: 'years' | 'quarters' | 'months' ) {
        let filteredPeriods = this.pricePeriodsByDateType[dateType];

        if (filteredPeriods.length == 0) {
            return false;
        }

        return filteredPeriods.every( (period) => this.pricePeriodIsSelected(period) );
    };

    // Toggles a year in the display selection while respecting any associated price covers.
    toggleYear(year: number) {
        let pricesInterval = this.selectedPricesTotalInterval;
        
        if (pricesInterval) {
            if (isWithinInterval(startOfYear(setYear(new Date(), year)), pricesInterval)) {
                this._snackbarService.callSnackBar('YEAR_HAS_ASSOCIATED_COVERS', SnackBarType.WARNING)
            } else {
                this.displayYearSelectionModel.toggle(year);
            }
        } else {
            this.displayYearSelectionModel.toggle(year);
        }
        
    }

    // Checks whether a date bucket is partially selected.
    pricePeriodByMarketTypeAndDateTypeIsIndeterminate( dateType: 'years' | 'quarters' | 'months' ) {
        let filteredPeriods = this.pricePeriodsByDateType[dateType];

        if (filteredPeriods.length == 0) {
            return false;
        }

        return filteredPeriods.some( (pricePeriod) => this.pricePeriodIsSelected(pricePeriod) ) && !filteredPeriods.every( (pricePeriod) => this.pricePeriodIsSelected(pricePeriod) );
    };

    // Determines whether a specific price period is selected.
    pricePeriodIsSelected( pricePeriod?: PricePeriods.ElectricityPrices, id?: string ) {
        if (pricePeriod) {
            return this.pricePeriodSelectionModel.isSelected(pricePeriod);
        } else {
            return this.pricePeriodSelectionModel.selected.find((price) => price.id == id) != null;
        }
    };

    // Toggles the selection state of a price period.
    togglePricePeriodSelection( pricePeriod: PricePeriods.ElectricityPrices ) {
        this.pricePeriodSelectionModel.toggle(pricePeriod);
    };

    // Opens the summary dialog with the relevant cover proposal data for the selected opportunity.
    showCoversSummaryDataDialog() {
        let filterBody = new ISAPI( [
            new ISFilterAPI('opportunity', this.opportunityId, 'text', FilterOperator.equals, undefined, 'FkEnergyOpportunityElectricityMeterNavigation;FkOpportunityNavigation', undefined, 'GuidId')
        ],
        new ISPagination(100, 1)
        )

        this._dialog.open(
            CoverProposalDialog, 
            { 
                data: {
                    equipments: this.currentEquipments,
                    economicConditions: this.economicConditionsFilterForm.value,
                    selectedPrices: this.selectedPrices,
                    opportunityId: this.opportunityId,
                    contractId: this.contractId,
                    series: this.simulationChartOptions.series,
                    selectedMarket: this.selectedMarket,
                    contractTypeId: environment.contractTypeIds.Personal,
                    // Display-only data so the dialog can recap the choices with the same wording as this page
                    summary: {
                        prices: this.selectedPrices.map((price) => ({
                            id: price.id,
                            label: this.getFriendlyPeriodLabel(price),
                            description: price.description,
                            coverPercentage: price.coverPercentage,
                            price: price.basePrice
                        })),
                        years: this.consumptionsByDateTypes
                            .filter((yearData) => yearData.offerCoveredConsumption > 0)
                            .map((yearData) => ({
                                year: yearData.year,
                                coverPercentage: yearData.coverPercentage,
                                totalCostEstimate: yearData.totalCostEstimate,
                                averagePriceEstimate: yearData.averagePriceEstimate
                            }))
                    }
                },
                width: '760px',
                maxWidth: '95vw'
            }
        ).afterClosed().subscribe( (returnResult) => {
            // Only a created proposal id navigates; cancelling returns nothing.
            // The proposals page shows opportunity proposals, so a contract proposal stays here (the dialog already confirmed it)
            if (returnResult != null && returnResult !== false && returnResult !== '' && !this.contractId) {
                this._router.navigateByUrl(`proposals/${returnResult}`);
            }
        });        
    }

    // The full history can span years of daily quotes, but only the recent part is read: the chart shows the last month,
    // calculateOmipTrend compares the latest quote with the one closest to a month before it, and the price matching uses the latest.
    // Two months before the latest quote leaves margin for the trend's closest-match search.
    private trimOmipHistory(history: IElectricityOmipPricesPTHistory[]): IElectricityOmipPricesPTHistory[] {
        const latest = _.maxBy(history, (h) => h.priceDate.getTime());

        if (!latest) {
            return [];
        }

        const keepFrom = Math.min(subMonths(latest.priceDate, 2).getTime(), subMonths(startOfDay(new Date()), 1).getTime());

        return history.filter((h) => h.priceDate.getTime() >= keepFrom);
    }

    // Loads OMIP historical prices and attaches them to the matching active price periods.
    getOMIPPrices() {
        this._priceService.getElectricityOMIPPrices().pipe(
            switchMap( (sr: ServerResponse<any[]>) => {
                return forkJoin(
                    sr.data.map( (price) => this._priceService.getElectricityOMIPPrice(price.id).pipe(
                        map( (detailResponse) => {
                            return new IElectricityOmipPricesPT(
                                price.id,
                                detailResponse.data.deliveryEndDate,
                                detailResponse.data.deliveryStartDate,
                                price.description,
                                price.lastPrice_MW,
                                price.lastPrice,
                                this.trimOmipHistory(detailResponse.data.electricityOmipPricesHistory.map( (history) => new IElectricityOmipPricesPTHistory(
                                    history.id,
                                    history.price,
                                    history.price_MW,
                                    history.priceDate
                                ))),
                            )
                        })
                    ) )
                );
            })
        ).subscribe( (sr: IElectricityOmipPricesPT[]) => {
            // Products already delivered are never shown nor matched (electricity prices only cover future periods), so they aren't kept
            const today = startOfDay(new Date());
            const omipPrices = _.sortBy(sr.filter( (omipPrice) => omipPrice.deliveryEndDate >= today ), ['deliveryStartDate']);

            let dateIntervalsToShow = {
                currentQuarter: {start: startOfQuarter(new Date()), end: startOfDay(endOfQuarter(new Date()))},
                year: {start: addYears(startOfYear(new Date()), 1), end: startOfDay(endOfYear(addYears(startOfYear(new Date()), 1)))},
                quarter: {start: addQuarters(startOfQuarter(new Date()), 1), end: startOfDay(endOfQuarter(addQuarters(startOfQuarter(new Date()), 1)))},
                month: {start: addMonths(startOfMonth(new Date()), 1), end: startOfDay(endOfMonth(addMonths(startOfMonth(new Date()), 1)))}
            }
            
            const omipPricesByInterval = omipPrices.reduce<{_1_currentQuarter: IElectricityOmipPricesPT | null, _2_year: IElectricityOmipPricesPT | null, _3_quarter: IElectricityOmipPricesPT | null, _4_month: IElectricityOmipPricesPT | null}>( (acc, omipPrice) => {
                if (acc._1_currentQuarter != null && acc._2_year != null && acc._3_quarter != null && acc._4_month != null ) {
                    return acc;
                }

                if (acc._1_currentQuarter == null && isSameDay(omipPrice.deliveryStartDate, dateIntervalsToShow.currentQuarter.start) && isSameDay(omipPrice.deliveryEndDate, dateIntervalsToShow.currentQuarter.end)) {
                    acc._1_currentQuarter = omipPrice;
                } else if (acc._2_year == null && isSameDay(omipPrice.deliveryStartDate, dateIntervalsToShow.year.start) && isSameDay(omipPrice.deliveryEndDate, dateIntervalsToShow.year.end)) {
                    acc._2_year = omipPrice;
                } else if (acc._3_quarter == null && isSameDay(omipPrice.deliveryStartDate, dateIntervalsToShow.quarter.start) && isSameDay(omipPrice.deliveryEndDate, dateIntervalsToShow.quarter.end)) {
                    acc._3_quarter = omipPrice;
                } else if (acc._4_month == null && isSameDay(omipPrice.deliveryStartDate, dateIntervalsToShow.month.start) && isSameDay(omipPrice.deliveryEndDate, dateIntervalsToShow.month.end)) {
                    acc._4_month = omipPrice;
                }

                return acc;
            }, {_1_currentQuarter: null, _2_year: null, _3_quarter: null, _4_month: null});
            
            this.omipIntervalPriceData = omipPricesByInterval;
            this.omipPrices = omipPrices;
            this.omipTrendsById = new Map(
                Object.values(omipPricesByInterval)
                    .filter((omipPrice): omipPrice is IElectricityOmipPricesPT => !!omipPrice)
                    .map((omipPrice) => [omipPrice.id, this.calculateOmipTrend(omipPrice)])
            );

            this.selectOmipPrice(omipPricesByInterval._1_currentQuarter || omipPricesByInterval._2_year || omipPricesByInterval._3_quarter || omipPricesByInterval._4_month);
            this.mapOmipToElectricityPrices();
        } )
    }

    mapOmipToElectricityPrices() {
        this.currentElectricityPrices.forEach( (elec) => {
            let matchingOmipPrices = this.omipPrices.find( (omipPrice) => isSameDay(omipPrice.deliveryStartDate, elec.deliveryStartDate) && isSameDay(omipPrice.deliveryEndDate, elec.deliveryEndDate))
            elec.omipElectricityHistory = _.maxBy(matchingOmipPrices?.electricityOmipPricesHistory, (omipPrice) => omipPrice.priceDate.getTime());
        })
    }

    selectOmipPrice(omipPrice: IElectricityOmipPricesPT | null) {
        if (!omipPrice) {
            return;
        }

        const omipHistoryInterval = { start: subMonths(startOfDay(new Date()), 1), end: new Date() }

        const series: any[] = [
            {
                name: omipPrice.description,
                data: omipPrice.electricityOmipPricesHistory
                    .filter( (price) => isWithinInterval(price.priceDate, omipHistoryInterval) )
                    .map( (price) => ({ x: price.priceDate.getTime(), y: EnergyUnits.convertPrice(price.price_MW, this.energyUnitService.priceUnit) }) )
            }
        ] 
        
        this.selectedOmipPrice = omipPrice;
        this.omipPriceChartOptions.series = series;
    }

    // Returns the full date interval for all selected price periods.
    get selectedPricesTotalInterval(): Interval | undefined {
        if (this.selectedPrices.length == 0) {
            return undefined;
        }

        let start = _.minBy(this.selectedPrices, (price) => price.deliveryStartDate)?.deliveryStartDate ?? 0
        let end = _.maxBy(this.selectedPrices, (price) => price.deliveryEndDate)?.deliveryEndDate ?? 0

        if (differenceInMonths(end, start) < 11) {
            if (getYear(end) == getYear(start)) {
                end = endOfYear(start)
            }
            start = startOfYear(start);
        }

        return { 
            start: start,
            end: end
        }
    }

    // Filters price periods according to the active market type.
    get filteredPricePeriodByMarketType(): PricePeriods.ElectricityPrices[] {
        if (this.selectedMarket == 'Electricity') {
            return this.currentElectricityPrices;
        } else {
            // return this.currentNaturalGasPrices;
            return [];
        }
    }

    // Filters equipment lists to match the selected market.
    get filteredCurrentEquipmentsByMarketType(): GroupingSimulation.IEquipment[] {
        if (this.selectedMarket == 'Electricity') {
            return this.currentEquipments.filter((eq) => eq instanceof GroupingSimulation.ElectricityEquipment);
        } else {
            return this.currentEquipments.filter((eq) => eq instanceof GroupingSimulation.NaturalGasEquipment);
        }
    }

    get isContractSelected(): boolean {
        return !String.isNullOrUndefinedOrWhitespace(this.contractId);
    }

    // Offers are narrowed to the conditions of the picked price; never on a contract.
    get isEconomicConditionsFilterActive(): boolean {
        return !this.isContractSelected && this.economicConditionsFilterForm.controls.spGeneralConditionId.value != null;
    }

    // Returns electricity price periods that are still available for the selected years and have no cover yet.
    get currentElecPricesByEconomicConditionFilter() {
        let filterPredicate = (pp: PricePeriods.ElectricityPrices) => true;

        // On a contract the offers aren't filtered by economic conditions
        if (this.isEconomicConditionsFilterActive) {
            filterPredicate = (pp: PricePeriods.ElectricityPrices) => 
                pp.spGeneralConditionId == this.economicConditionsFilterForm.controls.spGeneralConditionId.value && 
                pp.spParticularConditionId == this.economicConditionsFilterForm.controls.spParticularConditionId.value &&
                pp.supplierProductId == this.economicConditionsFilterForm.controls.supplierProductId.value && 
                (this.economicConditionsFilterForm.controls.spElectricityEconomicConditionId.value == null
                    || pp.spElectricityEconomicConditionId == this.economicConditionsFilterForm.controls.spElectricityEconomicConditionId.value);
        }
        return this.currentElectricityPrices.filter(
            (pp) => 
                pp.coverPercentage == 0 && 
                (this.selectedYears.includes(pp.deliveryStartDate.getFullYear()) || this.selectedYears.includes(pp.deliveryEndDate.getFullYear())) &&
                filterPredicate(pp)
        );
    }

    // Returns all electricity equipment objects in the current grouping.
    getElectricityEquipments(): GroupingSimulation.ElectricityEquipment[] {
        return (this.currentEquipments.filter((equip) => Object.hasOwn(equip, 'tensionLevelId')) as GroupingSimulation.ElectricityEquipment[]) ;
    }


    // Returns all natural gas equipment objects in the current grouping.
    getNaturalGasEquipments(): GroupingSimulation.NaturalGasEquipment[] {
        return (this.currentEquipments.filter((equip) => Object.hasOwn(equip, 'pressureLevelId')) as GroupingSimulation.NaturalGasEquipment[]);
    }

    // Returns the fixed-price simulation record for the given equipment and price period.
    getEquipmentPriceDataByPriceId(equipment: GroupingSimulation.IEquipment, priceId: string): GroupingSimulation.IFixedPriceSimulation | undefined {
        return equipment.priceData.find( (price) => price.priceId == priceId);
    }

    // Formats the delivery period of a price period into a readable date range for the UI.
    getPriceTimelineString(price: PricePeriods.ElectricityPrices, dateType: 'years' | 'quarters' | 'months' | undefined) {

        if (!dateType) {
            if (price.deliveryStartDate == startOfYear(price.deliveryStartDate) && price.deliveryEndDate == endOfYear(price.deliveryEndDate)) {
                dateType = 'years'
            } else if (price.deliveryStartDate == startOfQuarter(price.deliveryStartDate) && price.deliveryEndDate == endOfQuarter(price.deliveryEndDate)) {
                dateType = 'quarters'
            } else {
                dateType = 'months'
            }
        }
        
        if (dateType == 'years') {
            return _.uniq([price.deliveryStartDate, price.deliveryEndDate].map( (date) => format(date, 'y', { locale: this._userService.userCountryLocale }))).join(' - ');
        } else if (dateType == 'quarters') {
            return _.uniq([price.deliveryStartDate, price.deliveryEndDate].map( (date) => format(date, 'QQQ y', { locale: this._userService.userCountryLocale }))).join(' - ');
        } else {
            return _.uniq([price.deliveryStartDate, price.deliveryEndDate].map( (date) => format(date, 'MMM y', { locale: this._userService.userCountryLocale }))).join(' - ');
        }
    }

    // Returns the locale used to display dates in the user’s country format.
    get dateFormatLocale(): Locale {
        if (this._userService.userCountryCode == 'PT') {
            return pt;
        } else {
            return es;
        }
    }

    // Returns the exact month interval affected by the selected price periods within a given year.
    getMonthsWithPricesAppliedInYear(year: number): Interval {
        let yearInterval = {start: startOfYear(setYear(new Date(), year)), end: endOfYear(setYear(new Date(), year))};

        let pricesAppliedToYear = this.selectedPrices.filter( (price) => areIntervalsOverlapping({start: new Date(price.deliveryStartDate), end: new Date(price.deliveryEndDate)}, yearInterval));

        let pricesInterval = { start: _.minBy(pricesAppliedToYear, (price) => price.deliveryStartDate)?.deliveryStartDate ?? yearInterval.start, end: _.maxBy(pricesAppliedToYear, (price) => price.deliveryEndDate)?.deliveryEndDate ?? yearInterval.end };

        if (pricesInterval.start < yearInterval.start) {
            pricesInterval.start = yearInterval.start;
        }
        if (pricesInterval.end > yearInterval.end) {
            pricesInterval.end = yearInterval.end;
        }

        return pricesInterval;
    }
}